import { useEffect, useRef, useCallback } from "react";

import { FieldValues, UseFormReturn } from "react-hook-form";

const MINIMUM_SAVING_TIME = 2000;
const COOLDOWN_PERIOD = 5000;
const SAVE_DEBOUNCE_DELAY = 1500; // Wait for user to stop typing before saving

export const useAutoSave = <T extends FieldValues>({
  rawSave,
  form,
  resetForm,
  savingState,
  setSavingState,
  onSaved,
}: {
  form: UseFormReturn<T>;
  rawSave: () => Promise<void>;
  resetForm: () => void;
  savingState: "saving" | "saved" | "error" | undefined;
  setSavingState: (state: "saving" | "saved" | "error" | undefined) => void;
  onSaved: () => void;
}) => {
  const { isDirty } = form.formState;
  const isSavingRef = useRef<boolean>(false);

  const savingStartTimeRef = useRef<number>(0);
  const lastErrorTimeRef = useRef<number>(0);

  const saveDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const attemptSave = useCallback(async () => {
    await rawSave();
    const elapsedTime = Date.now() - savingStartTimeRef.current;
    if (elapsedTime < MINIMUM_SAVING_TIME) {
      await new Promise((resolve) => setTimeout(resolve, MINIMUM_SAVING_TIME - elapsedTime));
    }
    setSavingState("saved");
    onSaved();
    resetForm();
  }, [resetForm, rawSave, setSavingState, onSaved]);

  const performSaveWithRetry = useCallback(async () => {
    if (isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    try {
      await attemptSave();
    } catch {
      try {
        await attemptSave();
      } catch {
        setSavingState("error");
        lastErrorTimeRef.current = Date.now();
      }
    } finally {
      isSavingRef.current = false;
    }
  }, [attemptSave, setSavingState]);

  useEffect(() => {
    const handleSave = () => {
      // Clear any existing debounce timer
      if (saveDebounceTimerRef.current) {
        clearTimeout(saveDebounceTimerRef.current);
        saveDebounceTimerRef.current = null;
      }

      if (!isDirty || isSavingRef.current) {
        return;
      }

      if (savingState === "error" && Date.now() - lastErrorTimeRef.current < COOLDOWN_PERIOD) {
        return;
      }

      // Debounce the rawSave to wait for user to stop typing
      saveDebounceTimerRef.current = setTimeout(() => {
        setSavingState("saving");
        savingStartTimeRef.current = Date.now();
        performSaveWithRetry();
      }, SAVE_DEBOUNCE_DELAY);
    };

    handleSave();
    const subscription = form.watch(handleSave);

    // Cleanup subscription and timer on unmount
    return () => {
      subscription.unsubscribe();
      if (saveDebounceTimerRef.current) {
        clearTimeout(saveDebounceTimerRef.current);
        saveDebounceTimerRef.current = null;
      }
    };
  }, [form, isDirty, savingState, setSavingState, performSaveWithRetry]);

  return { savingState, setSavingState };
};
